import React, { useCallback } from 'react';

import { useTranslation } from '@lib/useTypedTranslation';
import Card from '../../../../components/card/card';
import { InitialTableState, useTableReducer } from '../../../../components/data-table/lib';
import { List } from '../../../../components/list/list';
import { BreadcrumbLink } from '../../../../components/navigation/breadcrumbLink';
import { getFormatZeroAsLessThanOneToLocale } from '../../../../components/data-table/dataFormatters';
import { useLanguageAndLocaleContext } from '../../../../context/languageAndLocale';
import { useUserSettingsContext } from '../../../../context/userSettings';
import { useWorldRequest } from '../../../../lib/useWorldRequest';
import { useWorldAction } from '../../../../lib/useWorldAction';
import { useWorldRoutes } from '../../../../routes/parts/allWorldRoutes';
import { getUserTimeZone } from '../../../../lib/timeZone';
import { formatDateAsOrdinalWithFullMonthAndYear } from '../../../../lib/ordinal';
import { convertFilterToQuery, IFilterResult } from '../../../../components/filters/filterLib';
import { FilterContext } from '@components/data-table/filters/context';
import {
  IPerformanceUtilizationDeviceRow,
  IPerformanceUtilizationDevicesListData,
  getCorePerformanceUtilizationDevicesList,
  exportCorePerformanceUtilizationDevicesListAction
} from '../../../../services/core/performanceUtilizationDevices';
import { useCorePerformanceDevicesColumns } from './useCorePerformanceDevicesColumns';
import { useCorePerformanceDevicesFilterOptions } from './filters/useCorePerformanceDevicesFilterOptions';
import useNormalizedHeaderButtons from '../../../../components/controls/normalizedHeaderButtons';
import { getCorePerformanceDevicesColumnFromFilterGroup, getInitialCorePerformanceDevicesFilter } from './corePerformanceEventDevices';

import './corePerformanceEventDevices.css';

interface IProps {
  utc: number,
  homeLocationId?: string | null,
  group?: string | null,
  zoneId?: string | null,
  model?: string | null
}

const tableName = 'corePerformanceUtilizationDevicesList';
const defaultPageSize = 20;

export function CorePerformanceUtilizationDevices(props: IProps) {
  const { utc, homeLocationId, group, zoneId, model } = props;
  const timeZone = getUserTimeZone();
  const { t } = useTranslation(['translation', 'performance']);
  const routes = useWorldRoutes();
  const { tablePageSizes } = useUserSettingsContext();
  const { locale } = useLanguageAndLocaleContext();

  const eventName = t('UTILISATION', { ns: 'performance' });
  const date = formatDateAsOrdinalWithFullMonthAndYear(utc, timeZone);
  const title = t('PERFORMANCE_DEVICES_TITLE', { ns: 'performance', eventName, date });

  const columns = useCorePerformanceDevicesColumns({
    countColumnTitle: t('UTILISATION_MINUTES', { ns: 'performance' }),
    columnId: 'used',
    columnDataPath: 'used',
    valueProcessor: getFormatZeroAsLessThanOneToLocale(locale)
  });

  const initialTableState: InitialTableState = {
    initialRows: 4,
    limit: tablePageSizes?.[tableName] || defaultPageSize,
    sort: { column: columns.findIndex((c) => c.id === 'used'), direction: 'desc', field: 'used' },
    filter: getInitialCorePerformanceDevicesFilter(homeLocationId, group, model)
  };

  const [tableReducerProperties, tableReducerFunctions] = useTableReducer<IPerformanceUtilizationDeviceRow>(tableName, initialTableState);
  const [
    { offset, limit, sort, filter, data, isLoading },
    { onFilter, onDataLoaded }
  ] = [tableReducerProperties, tableReducerFunctions];

  const listFetcher = useCallback(() => {
    return getCorePerformanceUtilizationDevicesList({
      utc,
      timeZone,
      zoneId,
      limit,
      offset,
      sort: { field: sort.field, order: sort.direction },
      filter: convertFilterToQuery(filter)
    });
  }, [utc, timeZone, zoneId, limit, offset, sort, filter]);

  const exportCorePerformanceDevices = useWorldAction(exportCorePerformanceUtilizationDevicesListAction);
  const onExportClicked = async (): Promise<string> => {
    return exportCorePerformanceDevices({
      utc,
      timeZone,
      zoneId,
      limit: 10000,
      offset: 0,
      sort: { field: sort.field, order: sort.direction },
      filter: convertFilterToQuery(filter)
    });
  };

  const additionalHeaderButtons = useNormalizedHeaderButtons({
    export: {
      exportFunction: onExportClicked,
      filename: `${title}.csv`,
      disabled: isLoading || !data?.length
    }
  });

  const { availableFilters, filtersLoaded } = useCorePerformanceDevicesFilterOptions();

  const handleFiltersUpdated = (newFilter: IFilterResult) => {
    onFilter(newFilter);
  };
  const handleCancel = (): void => { /* no-op */ };

  const translationItems = {
    statusText: {
      key: 'DEVICE',
      ns: 'translation'
    },
    tableTitle: {
      text: title
    }
  };

  const breadcrumb = (
    <div data-id="core-performance-devices-breadcrumb" className="core-performance-devices-breadcrumb">
      <BreadcrumbLink to={routes.core.performance} aria-label="Link to performance" className="core-performance-devices-breadcrumb-link">{t('PERFORMANCE', { ns: 'translation' })}</BreadcrumbLink>
      <i className="fas fa-chevron-right" />
      <div className="core-performance-devices-event-name">{eventName}</div>
    </div>
  );

  return (
    <FilterContext.Provider value={{
      getColumnFromFilterGroup: getCorePerformanceDevicesColumnFromFilterGroup,
      filters: filter,
      filterOptions: { availableFilters, filtersLoaded },
      handleFiltersUpdated,
      handleCancel
    }}>
      <div>
        <div data-id="core-performance-devices" className="core-performance-list">
          <Card noPadding={true}>
            <List<IPerformanceUtilizationDeviceRow, IPerformanceUtilizationDevicesListData, 'totalCount', 'list', 'id'>
              dataId='core_performance_devices_list'
              dataTableEnabled={true}
              customHeader={false}
              countDataPath='totalCount'
              listDataPath='list'
              hasCheckboxes={false}
              hasFilters={true}
              hasSearch={false}
              translationItems={translationItems}
              subHeader={breadcrumb}
              columns={columns}
              fetcher={listFetcher}
              tableReducerFunctions={tableReducerFunctions}
              tableReducerProperties={tableReducerProperties}
              useRequestHook={useWorldRequest}
              options={{ rowIdDataPath: 'id' }}
              additionalHeaderButtons={additionalHeaderButtons}
            />
          </Card>
        </div>
      </div>
    </FilterContext.Provider>
  );
}
